import { useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowLeft,
    Utensils,
    PlusCircle,
    CheckCircle2,
    ChevronUp,
} from "lucide-react";

/* =========================
   CONFIGURAZIONE
========================= */

const BASE_PRICE = 8.0;
const HEADER_HEIGHT = 48;

/* =========================
   INGREDIENTI
========================= */

const INGREDIENT_CATEGORIES = [
    {
        id: "salumi",
        label: "Salumi",
        max: 1,
        items: [
            { id: 1, name: "Capocollo", price: 1.0 },
            { id: 2, name: "Mortadella", price: 1.0 },
            { id: 3, name: "Speck", price: 1.0 },
            { id: 4, name: "Bacon", price: 1.0 },
        ],
    },
    {
        id: "formaggi",
        label: "Formaggi",
        max: 1,
        items: [
            { id: 5, name: "Scamorza", price: 1.0 },
            { id: 6, name: "Caciocavallo", price: 1.0 },
            { id: 7, name: "Burratina", price: 2.5 },
            { id: 8, name: "Burratina affumicata", price: 3.0 },
        ],
    },
    {
        id: "ortaggi",
        label: "Ortaggi",
        max: 3,
        items: [
            { id: 9, name: "Insalata", price: 0.5 },
            { id: 10, name: "Rucola", price: 0.5 },
            { id: 11, name: "Pomodorini", price: 0.5 },
            { id: 12, name: "Pomodori semi dry", price: 1.0 },
            { id: 13, name: "Funghi champignon", price: 1.0 },
            { id: 14, name: "Funghi chiodini", price: 1.0 },
        ],
    },
    {
        id: "salse",
        label: "Salse",
        max: 3,
        items: [
            { id: 15, name: "Ketchup", price: 0.5 },
            { id: 16, name: "Maionese", price: 0.5 },
            { id: 17, name: "Salsa cocktail", price: 0.5 },
            { id: 18, name: "Barbecue", price: 0.5 },
            { id: 19, name: "Cheddar", price: 0.5 },
            { id: 20, name: "Pesto basilico", price: 1.0 },
            { id: 21, name: "Pesto pistacchio", price: 1.0 },
            { id: 22, name: "Crema di 'nduja", price: 1.0 },
        ],
    },
    {
        id: "contorni",
        label: "Contorno",
        max: 1,
        items: [
            { id: 23, name: "Patate stick", price: 1.5 },
            { id: 24, name: "Patate dippers", price: 1.5 },
        ],
    },
    {
        id: "carne",
        label: "Carne",
        max: 1,
        items: [
            { id: 25, name: "Double hamburger", price: 5.0 },
        ],
    },
];

/* =========================
   COMPONENTE
========================= */

export default function CreaHamburger() {
    const navigate = useNavigate();

    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [shakeCategory, setShakeCategory] = useState(null);
    const [isTotalOpen, setIsTotalOpen] = useState(false);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    /* =========================
       LOGICA
    ========================= */

    const toggleIngredient = (ingredient, category) => {
        const isSelected = selectedIngredients.some(
            (i) => i.id === ingredient.id
        );

        if (isSelected) {
            setSelectedIngredients((prev) =>
                prev.filter((i) => i.id !== ingredient.id)
            );
            return;
        }

        const selectedInCategory = selectedIngredients.filter((i) =>
            category.items.some((item) => item.id === i.id)
        );

        if (category.max && selectedInCategory.length >= category.max) {
            setShakeCategory(category.id);
            navigator.vibrate?.(50);

            setTimeout(() => {
                setShakeCategory(null);
            }, 2000);

            return;
        }

        setSelectedIngredients((prev) => [...prev, ingredient]);
    };

    const extrasTotal = selectedIngredients.reduce(
        (sum, item) => sum + item.price,
        0
    );

    const totalPrice = BASE_PRICE + extrasTotal;

    /* =========================
       RENDER
    ========================= */

    return (
        <main className="min-h-safe-screen bg-[#0D111A] text-secondary pb-36 pt-[64px]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-surface px-4 py-3 border-b border-accent shadow-md">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => {
                            if (window.history.length > 2) {
                                navigate(-1);
                            } else {
                                navigate("/menu");
                            }
                        }}
                        className="text-secondary p-2"
                        aria-label="Torna al menu"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div className="flex items-center gap-2 text-primary text-sm font-bold truncate">
                        <strong>{"Puccia Hamburger"}</strong>
                    </div>
                    <div className="w-6" />
                </div>
            </header>

            {/* CONTENUTO */}
            <motion.section className="px-6 max-w-md mx-auto space-y-6 pt-6">

                <div>
                    <h3 className="flex gap-2 items-center text-primary font-semibold mb-2">
                        <Utensils className="w-4 h-4" /> Base inclusa
                    </h3>
                    <div className="flex justify-between text-sm">
                        <span>Puccia artigianale +<br />Hamburger di Scottona 200 gr</span>
                        <span className="text-primary font-medium">
                            € {BASE_PRICE.toFixed(2)}
                        </span>
                    </div>
                </div>

                <div>
                    <h3 className="flex gap-2 items-center text-primary font-semibold mb-4">
                        <PlusCircle className="w-4 h-4" /> Ingredienti extra
                    </h3>

                    {INGREDIENT_CATEGORIES.map((category) => (
                        <motion.div
                            key={category.id}
                            animate={
                                shakeCategory === category.id
                                    ? { x: [-6, 6, -6, 6, 0] }
                                    : {}
                            }
                        >
                            <h4 className="text-primary font-semibold mt-4 mb-2">
                                {category.label} (max {category.max})
                            </h4>

                            <ul className="space-y-3">
                                {category.items.map((item) => {
                                    const isSelected =
                                        selectedIngredients.some(
                                            (i) => i.id === item.id
                                        );

                                    return (
                                        <li
                                            key={item.id}
                                            onClick={() =>
                                                toggleIngredient(
                                                    item,
                                                    category
                                                )
                                            }
                                            className={`flex justify-between items-center px-4 py-3 rounded-xl border text-sm transition
                                            ${isSelected
                                                    ? "bg-primary/10 border-primary"
                                                    : "border-accent hover:bg-surface"
                                                }`}
                                        >
                                            <div className="flex gap-2 items-center">
                                                {isSelected && (
                                                    <CheckCircle2 className="w-4 h-4 text-primary" />
                                                )}
                                                {item.name}
                                            </div>
                                            <span className="text-primary font-medium">
                                                + € {item.price.toFixed(2)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </motion.div>
                    ))}
                </div>
            </motion.section>

            {/* TOTALE / ACCORDION */}
            <AnimatePresence>
                <motion.div
                    initial={false}
                    animate={{
                        height: isTotalOpen
                            ? `calc(100vh - ${HEADER_HEIGHT}px)`
                            : 72,
                        top: isTotalOpen ? HEADER_HEIGHT : "auto",
                    }}
                    transition={{
                        type: "spring",
                        stiffness: 120,
                        damping: 20,
                    }}
                    drag={isTotalOpen ? "y" : false}
                    dragConstraints={{ top: 0, bottom: 0 }}
                    dragElastic={0.15}
                    onDragEnd={(_, info) => {
                        if (info.offset.y > 80) {
                            setIsTotalOpen(false);
                        }
                    }}
                    className="fixed bottom-0 inset-x-0 bg-surface border-t border-accent z-40 overflow-hidden touch-pan-y"
                >
                    {/* HEADER ACCORDION */}
                    <div className="flex justify-between items-center px-6 py-4">
                        <button
                            onClick={() => setIsTotalOpen((p) => !p)}
                            className="flex items-center gap-2"
                        >
                            <ChevronUp
                                className={`transition-transform duration-300 ${isTotalOpen ? "rotate-180" : ""
                                    }`}
                            />
                            <span className="text-muted">Totale</span>

                            {selectedIngredients.length > 0 && (
                                <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                                    {selectedIngredients.length}
                                </span>
                            )}
                        </button>

                        <span className="text-xl font-bold text-primary">
                            € {totalPrice.toFixed(2)}
                        </span>
                    </div>

                    {/* RIEPILOGO */}
                    {isTotalOpen && (
                        <div className="px-6 pt-4 pb-24 space-y-3 overflow-y-auto">
                            {selectedIngredients.length === 0 && (
                                <p className="text-muted text-sm">
                                    Nessun ingrediente extra selezionato
                                </p>
                            )}

                            {selectedIngredients.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex justify-between items-center text-sm"
                                >
                                    <span>{item.name}</span>
                                    <button
                                        onClick={() =>
                                            setSelectedIngredients((prev) =>
                                                prev.filter((i) => i.id !== item.id)
                                            )
                                        }
                                        className="text-primary font-medium"
                                    >
                                        − € {item.price.toFixed(2)}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>
        </main>
    );
}
